// src/pages/AdminPanel/Orders.jsx
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';
import Swal from 'sweetalert2'; // For displaying order details in a modal
import { Eye, Clock, CheckCircle, XCircle } from 'lucide-react'; // Icons
import './Orders.css'; // Custom CSS for premium styling

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use orderBy for chronological order (most recent first)
      const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setOrders(data);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchOrders(); 
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="status-badge status-completed flex items-center"><CheckCircle size={14} className="mr-1" /> Completed</span>;
      case 'pending':
        return <span className="status-badge status-pending flex items-center"><Clock size={14} className="mr-1" /> Pending</span>;
      case 'canceled':
        return <span className="status-badge status-canceled flex items-center"><XCircle size={14} className="mr-1" /> Canceled</span>;
      default:
        // Fallback for unknown statuses
        return <span className="status-badge status-unknown flex items-center">{status || 'Unknown'}</span>;
    }
  };

  const handleViewDetails = (order) => {
    Swal.fire({
      title: 'Order Details',
      html: `
        <div class="swal2-content-custom">
          <p><strong>Order ID:</strong> ${order.id}</p>
          <p><strong>Customer Email:</strong> ${order.customerEmail || 'N/A'}</p>
          <p><strong>Total Amount:</strong> $${(order.totalAmount / 100).toFixed(2)}</p>
          <p><strong>Status:</strong> ${order.status || 'N/A'}</p>
          <p><strong>Order Date:</strong> ${order.createdAt?.toDate ? order.createdAt.toDate().toLocaleString() : 'N/A'}</p>
          <h4 style="margin-top: 15px; margin-bottom: 10px; color: #a0aec0;">Items:</h4>
          <ul style="list-style: none; padding: 0;">
            ${order.items?.map(item => `
              <li style="margin-bottom: 5px; padding: 5px 0; border-bottom: 1px dashed #4a5568;">
                ${item.name} (x${item.quantity}) - $${(item.price / 100).toFixed(2)} each
              </li>
            `).join('') || 'No items found.'}
          </ul>
          <h4 style="margin-top: 15px; margin-bottom: 10px; color: #a0aec0;">Shipping Address:</h4>
          <p>${order.shippingAddress ? Object.values(order.shippingAddress).join(', ') : 'N/A'}</p>
        </div>
      `,
      showCloseButton: true,
      showConfirmButton: false,
      background: '#1a202c', // Dark background for Swal
      color: '#e2e8f0', // Light text for Swal
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom',
        htmlContainer: 'swal2-html-container-custom'
      }
    });
  };

  if (loading) return <div className="text-center py-10 text-gray-300 text-xl">Loading Orders...</div>;
  if (error) return <div className="text-center py-10 text-red-400 text-xl">{error}</div>;

  return (
    <div className="orders-container-main"> {/* Main container for page-level styling */}
      <h2 className="section-title">Order Management</h2> {/* Consistent title styling */}
      
      {orders.length === 0 ? (
        <div className="empty-state-message">No orders found.</div>
      ) : (
        <div className="orders-table-wrapper"> {/* Container for the table for shadow/border */}
          <table className="orders-table"> {/* The actual HTML table */}
            <thead>
              <tr className="table-header-row">
                <th className="px-6 py-3 text-left">Order ID</th>
                <th className="px-6 py-3 text-left">Date</th>
                <th className="px-6 py-3 text-left">Customer Email</th>
                <th className="px-6 py-3 text-left">Total Amount</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr className="table-row" key={o.id}>
                  <td className="px-6 py-4">{o.id.slice(0, 8)}...</td> {/* Truncate ID for display */}
                  <td className="px-6 py-4">{o.createdAt?.toDate ? o.createdAt.toDate().toLocaleString() : 'N/A'}</td>
                  <td className="px-6 py-4">{o.customerEmail || o.userId || 'N/A'}</td>
                  <td className="px-6 py-4">${(o.totalAmount / 100).toFixed(2)}</td> {/* Ensure totalAmount */}
                  <td className="px-6 py-4">{getStatusBadge(o.status)}</td>
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => handleViewDetails(o)}
                      className="view-details-button"
                    >
                      <Eye size={16} className="mr-1" /> View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Orders;
